import { BaseTable } from '@/modules';
import React, { ReactNode, useMemo } from 'react';
import { PORTAL_TARGETS } from '@/types/shared/navigation-tabs.enums';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { ColumnDef } from '@tanstack/react-table';

interface AssetPreviewTableProps<T> {
  data: T[];
  renderSearchInto?: PORTAL_TARGETS;
  emptyStateMessage?: { icon?: ReactNode; message?: ReactNode };
  columns: ColumnDef<T>[];
  rowClickHandler?: (data: T) => void;
  /**
   * The ID of the currently selected row. When this matches a row's ID (extracted via getRowId),
   * that row will be highlighted with a primary-colored background and border.
   */
  selectedRowId?: string | null;
  /**
   * Function to extract the unique ID from each row's data.
   * This allows the table to work with different data structures that have different ID field names
   * (e.g., fault_id, asset_id, id, uuid, etc.).
   * @example getRowId={(row) => row.fault_id}
   */
  getRowId?: (row: T) => string;
}

export const AssetPreviewTable = <T,>({
  data,
  renderSearchInto,
  emptyStateMessage,
  columns,
  rowClickHandler,
  selectedRowId,
  getRowId,
}: AssetPreviewTableProps<T>) => {
  const assetsWithTestIds = useMemo(
    () =>
      data.map((asset) => ({
        ...asset,
        // test_id: generateTestId(`tile_${asset.asset_ui_name}`),
        test_id: generateTestId(`tile_1`),
      })),
    [data]
  );

  return (
    <>
      <BaseTable
        enableSearch={true}
        enableSearchDropdown={false}
        pageSize={999999} // Show all data without pagination
        enableSorting={true}
        wrapHeaders={false}
        searchPlaceholder="Search asset name..."
        mobileVariant="none"
        // variant="spaced"
        data={assetsWithTestIds}
        columns={columns}
        enableSearchStateSync
        rowClickHandler={rowClickHandler}
        selectedRowId={selectedRowId}
        getRowId={getRowId}
        classNames={{
          card: {
            container: 'h-full w-full overflow-hidden flex flex-col',
            content: 'p-0 m-2 flex-1 overflow-hidden',
          },
          table: {
            container: 'overflow-x-auto overflow-y-auto h-full w-full scrollbar-thin max-h-[calc(100vh-14.5rem)]',
            th: '[&>*]:text-subtext [&>*]:font-medium [&>*]:text-xs py-2 first-of-type:w-[40px]',
            tr: 'h-14',
            td: 'group-hover:bg-gray-100 dark:group-hover:bg-[#2f3844] first-of-type:w-[40px] text-sm',
          },
        }}
        renderSearchInto={renderSearchInto}
        emptyStateMessage={emptyStateMessage}
        inputVariant="buttonWrapper"
      />
    </>
  );
};
